import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Slider } from "../components/ui/slider";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { 
  Activity, Play, RefreshCw, XCircle, Search, TrendingUp, TrendingDown,
  BarChart3, Clock, CheckCircle, AlertTriangle, TestTube, Settings,
  Calendar, Database, Zap, Timer, LineChart, Download, HelpCircle,
  Brain, Target, Shield, Gauge, RotateCcw, Rocket, Cloud
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Metric definitions for tooltips
const METRIC_DEFINITIONS = {
  expected_alpha: "Expected excess return (alpha) over the benchmark. Positive values indicate the fund-sector combination historically outperforms. Computed as the probability-weighted average of outcome bucket midpoints from the Bayesian model.",
  p_loss: "Probability of Loss - The estimated probability that the fund's buys in this sector will result in negative forward alpha. Lower is better. Sum of probabilities for outcome buckets below 1.0 (100%).",
  entropy: "A measure of uncertainty/randomness in the outcome distribution. Higher entropy = more uncertain outcomes. Lower entropy = more predictable (concentrated) outcomes. Range ~0 to ~2.1 (uniform).",
  kl_to_sector_base: "Kullback-Leibler divergence from the sector baseline. Measures how 'different' this fund's skill profile is from the average fund in the sector. Higher = more distinctive/differentiated strategy.",
  ess_proxy: "Effective Sample Size proxy - A measure of evidence strength. Higher values indicate more reliable estimates backed by more independent observations. Low ESS means estimates are heavily shrunk toward priors.",
};

// Bucket labels
const BUCKET_LABELS = [
  "< -30%", "-30% to -15%", "-15% to -5%", "-5% to 0%",
  "0% to 5%", "5% to 15%", "15% to 30%", "> 30%"
];

const SkillsPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [searchFund, setSearchFund] = useState("");
  const [searchSector, setSearchSector] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [config, setConfig] = useState({});
  const [configDirty, setConfigDirty] = useState(false);
  const [modalDirectDbLoad, setModalDirectDbLoad] = useState(false);  // Option C toggle
  const [observationDates, setObservationDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [funds, setFunds] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [selectedFund, setSelectedFund] = useState("");
  const [selectedSector, setSelectedSector] = useState("");
  const [runHistory, setRunHistory] = useState([]);
  const [skillData, setSkillData] = useState([]);  // For probability buckets tab
  const [skill6mData, setSkill6mData] = useState([]);  // For 6m probability buckets
  const [runLogs, setRunLogs] = useState([]);  // For logs tab
  const [selectedRunId, setSelectedRunId] = useState("");  // Selected run for logs
  const [preflightCheck, setPreflightCheck] = useState(null);  // Preflight check results
  const [preflightLoading, setPreflightLoading] = useState(false);
  
  // Quick Test parameters
  const [quickTestObsDates, setQuickTestObsDates] = useState(1);
  const [quickTestMaxFunds, setQuickTestMaxFunds] = useState(10);
  const [quickTestAdviSteps, setQuickTestAdviSteps] = useState(5000);
  const [quickTestLoading, setQuickTestLoading] = useState(false);
  
  // Modal cloud compute
  const [useModal, setUseModal] = useState(true);
  const [modalMemoryGb, setModalMemoryGb] = useState(128);
  const [modalCpu, setModalCpu] = useState(8);
  const [modalConfigLoaded, setModalConfigLoaded] = useState(false);  // Track if initial config loaded
  
  // Observation date tracking
  const [obsDateStatus, setObsDateStatus] = useState([]);
  const [obsDateLoading, setObsDateLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/fund-intelligence/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setStats(result);
        setRunning(result.state?.status === "running");
        if (result.config) {
          setConfig(result.config);
          // Only sync Modal settings on INITIAL load, not on every poll
          // This prevents overwriting user's slider changes
          if (!modalConfigLoaded) {
            if (result.config.use_modal !== undefined) {
              setUseModal(result.config.use_modal);
            }
            if (result.config.modal_memory_gb !== undefined) {
              setModalMemoryGb(result.config.modal_memory_gb);
            }
            if (result.config.modal_cpu !== undefined) {
              setModalCpu(result.config.modal_cpu);
            }
            if (result.config.modal_direct_db_load !== undefined) {
              setModalDirectDbLoad(result.config.modal_direct_db_load);
            }
            setModalConfigLoaded(true);
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchObservationDates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/fund-intelligence/observation-dates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setObservationDates(result.dates || []);
        if (result.dates?.length > 0 && !selectedDate) {
          setSelectedDate(result.dates[0]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch observation dates:", error);
    }
  }, [token, selectedDate]);

  const fetchFundsAndSectors = useCallback(async () => {
    try {
      const [fundsRes, sectorsRes] = await Promise.all([
        fetch(`${API}/fund-intelligence/funds`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/fund-intelligence/sectors`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);
      
      if (fundsRes.ok) {
        const fundsData = await fundsRes.json();
        setFunds(fundsData.funds || []);
      }
      if (sectorsRes.ok) {
        const sectorsData = await sectorsRes.json();
        setSectors(sectorsData.sectors || []);
      }
    } catch (error) {
      console.error("Failed to fetch funds/sectors:", error);
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    setDataLoading(true);
    try {
      // Clean the date - only pass YYYY-MM-DD format (strip time component)
      const cleanDate = selectedDate ? selectedDate.split('T')[0] : null;
      
      let url = `${API}/fund-intelligence/data?limit=100`;
      if (cleanDate) url += `&observation_date=${cleanDate}`;
      if (selectedFund) url += `&fund=${selectedFund}`;
      if (selectedSector) url += `&sector=${selectedSector}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setData(result.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setDataLoading(false);
    }
  }, [token, selectedDate, selectedFund, selectedSector]);

  const fetchSkillData = useCallback(async () => {
    try {
      // Clean the date - only pass YYYY-MM-DD format (strip time component)
      const cleanDate = selectedDate ? selectedDate.split('T')[0] : null;
      
      // Fetch 12m skills
      let url12m = `${API}/fund-intelligence/skill-12m?limit=100`;
      if (cleanDate) url12m += `&observation_date=${cleanDate}`;
      if (selectedFund) url12m += `&fund=${selectedFund}`;
      if (selectedSector) url12m += `&sector=${selectedSector}`;
      
      const response12m = await fetch(url12m, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response12m.ok) {
        const result = await response12m.json();
        setSkillData(result.data || []);
      }
      
      // Fetch 6m skills
      let url6m = `${API}/fund-intelligence/skill-6m?limit=100`;
      if (cleanDate) url6m += `&observation_date=${cleanDate}`;
      if (selectedFund) url6m += `&fund=${selectedFund}`;
      if (selectedSector) url6m += `&sector=${selectedSector}`;
      
      const response6m = await fetch(url6m, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response6m.ok) {
        const result = await response6m.json();
        setSkill6mData(result.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch skill data:", error);
    }
  }, [token, selectedDate, selectedFund, selectedSector]);

  const fetchRunHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API}/fund-intelligence/runs?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setRunHistory(result.runs || []);
        // Auto-select latest run for logs
        if (result.runs?.length > 0 && !selectedRunId) {
          setSelectedRunId(result.runs[0].run_id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch run history:", error);
    }
  }, [token, selectedRunId]);

  const fetchRunLogs = useCallback(async (runId) => {
    if (!runId) return;
    try {
      const response = await fetch(`${API}/fund-intelligence/runs/${runId}/logs?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setRunLogs(result.logs || []);
      }
    } catch (error) {
      console.error("Failed to fetch run logs:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
    fetchObservationDates();
    fetchFundsAndSectors();
    fetchRunHistory();
    
    const interval = setInterval(() => {
      fetchStats();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [fetchStats, fetchObservationDates, fetchFundsAndSectors, fetchRunHistory]);

  useEffect(() => {
    if (activeTab === "data") {
      fetchData();
    } else if (activeTab === "buckets") {
      fetchSkillData();
    } else if (activeTab === "logs") {
      fetchRunLogs(selectedRunId);
    }
  }, [activeTab, fetchData, fetchSkillData, fetchRunLogs, selectedDate, selectedFund, selectedSector, selectedRunId]);

  // Refresh logs while pipeline is running (every 2s for real-time PyMC progress)
  useEffect(() => {
    if (running && activeTab === "logs" && selectedRunId) {
      const interval = setInterval(() => {
        fetchRunLogs(selectedRunId);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [running, activeTab, selectedRunId, fetchRunLogs]);

  const fetchObsDateStatus = useCallback(async () => {
    setObsDateLoading(true);
    try {
      // Fetch processed dates
      const response = await fetch(`${API}/fund-intelligence/obs-dates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setObsDateStatus(result.obs_dates || []);
      }
      
      // Fetch available dates
      const availResponse = await fetch(`${API}/fund-intelligence/obs-dates/available`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (availResponse.ok) {
        const availResult = await availResponse.json();
        setAvailableDates(availResult.available_dates || []);
      }
    } catch (error) {
      console.error("Failed to fetch obs date status:", error);
    } finally {
      setObsDateLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === "obs-dates") {
      fetchObsDateStatus();
    }
  }, [activeTab, fetchObsDateStatus]);

  const handleDeleteObsDate = async (obsDate) => {
    if (!confirm(`Delete all data for ${obsDate}? This cannot be undone.`)) return;
    
    try {
      const response = await fetch(`${API}/fund-intelligence/obs-dates/${obsDate}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`Deleted data for ${obsDate}`);
        fetchObsDateStatus();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete");
      }
    } catch (error) {
      toast.error("Failed to delete observation date data");
    }
  };

  const handleReprocessObsDate = async (obsDate) => {
    try {
      const response = await fetch(`${API}/fund-intelligence/obs-dates/${obsDate}/reprocess`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success(`Started reprocessing ${obsDate}`);
        setRunning(true);
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start reprocessing");
      }
    } catch (error) {
      toast.error("Failed to start reprocessing");
    }
  };

  const handleRunSelectedDates = async () => {
    if (selectedDates.length === 0) {
      toast.error("Please select at least one observation date");
      return;
    }
    
    try {
      const response = await fetch(`${API}/fund-intelligence/run/dates`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(selectedDates)
      });
      if (response.ok) {
        toast.success(`Started processing ${selectedDates.length} observation date(s)`);
        setRunning(true);
        setSelectedDates([]);
        setActiveTab("logs");
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start");
      }
    } catch (error) {
      toast.error("Failed to start processing");
    }
  };

  const toggleDateSelection = (date) => {
    setSelectedDates(prev => 
      prev.includes(date) 
        ? prev.filter(d => d !== date)
        : [...prev, date]
    );
  };

  const handleRun = async (runType) => {
    try {
      const response = await fetch(`${API}/fund-intelligence/run/${runType}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success(`${runType} pipeline started`);
        setRunning(true);
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start pipeline");
      }
    } catch (error) {
      toast.error("Failed to start pipeline");
    }
  };

  const handleCancel = async () => {
    try {
      const response = await fetch(`${API}/fund-intelligence/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Cancel signal sent");
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to cancel");
    }
  };

  const handleForceReset = async () => {
    try {
      const response = await fetch(`${API}/fund-intelligence/force-reset`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Pipeline reset to idle");
        setRunning(false);
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to reset");
    }
  };

  const handleQuickTest = async () => {
    setQuickTestLoading(true);
    try {
      const params = new URLSearchParams({
        obs_dates: quickTestObsDates.toString(),
        max_funds: quickTestMaxFunds.toString(),
        advi_steps: quickTestAdviSteps.toString(),
        advi_samples: Math.floor(quickTestAdviSteps / 5).toString()
      });
      
      const response = await fetch(`${API}/fund-intelligence/run/quick-test?${params}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setRunning(true);
        
        // Fetch stats to get the new run_id, then switch to logs
        const statsRes = await fetch(`${API}/fund-intelligence/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (statsRes.ok) {
          const stats = await statsRes.json();
          if (stats?.state?.current_run_id) {
            setSelectedRunId(stats.state.current_run_id);
          }
        }
        
        setActiveTab("logs");  // Switch to logs to see progress
        fetchStats();
        fetchRunHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start quick test");
      }
    } catch (error) {
      toast.error("Failed to start quick test");
    } finally {
      setQuickTestLoading(false);
    }
  };

  const runPreflightCheck = async () => {
    setPreflightLoading(true);
    try {
      const response = await fetch(`${API}/fund-intelligence/preflight-check`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setPreflightCheck(result);
        if (result.status === "ready") {
          toast.success("All checks passed! Ready to run.");
        } else {
          toast.warning(`Found ${result.issues?.length || 0} issue(s). Check details.`);
        }
      }
    } catch (error) {
      toast.error("Failed to run preflight check");
    } finally {
      setPreflightLoading(false);
    }
  };

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setConfigDirty(true);
  };

  const saveConfig = async () => {
    try {
      const response = await fetch(`${API}/fund-intelligence/config`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(config),
      });
      if (response.ok) {
        toast.success("Configuration saved");
        setConfigDirty(false);
        fetchStats();
      } else {
        toast.error("Failed to save configuration");
      }
    } catch (error) {
      toast.error("Failed to save configuration");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", { 
        year: "numeric", 
        month: "short", 
        day: "numeric" 
      });
    } catch {
      return dateStr;
    }
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined || isNaN(num)) return "N/A";
    return Number(num).toFixed(decimals);
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined || isNaN(num)) return "N/A";
    return `${Number(num).toFixed(2)}%`;
  };

  const MetricWithTooltip = ({ label, value, metricKey, icon: Icon }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 cursor-help">
            {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
            <span className="text-sm text-muted-foreground">{label}</span>
            <HelpCircle className="w-3 h-3 text-muted-foreground/50" />
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-sm">{METRIC_DEFINITIONS[metricKey]}</p>
        </TooltipContent>
      </Tooltip>
      <p className="font-mono text-lg">{value}</p>
    </TooltipProvider>
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Fund Intelligence Engine</h1>
          <p className="text-muted-foreground">
            Bayesian fund-by-sector skill estimation and timing curves
          </p>
        </div>
        <div className="flex items-center gap-2">
          {running ? (
            <>
              <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">
                <Activity className="w-3 h-3 mr-1 animate-pulse" />
                Running
              </Badge>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                <XCircle className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={handleForceReset}
                data-testid="force-reset-btn"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Force Reset
              </Button>
            </>
          ) : (
            <Button 
              size="sm" 
              onClick={() => handleRun("full")}
              data-testid="run-full-btn"
            >
              <Play className="w-4 h-4 mr-1" />
              Full Run
            </Button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      {running && stats?.state?.progress && (
        <Card className="border-yellow-500/20 bg-yellow-500/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">{stats.state.progress.phase}</span>
              <span className="text-sm text-muted-foreground">
                {stats.state.progress.processed} / {stats.state.progress.total}
              </span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-yellow-500 transition-all duration-500"
                style={{ width: `${stats.state.progress.percent_complete}%` }}
              />
            </div>
            {stats.state.progress.current_item && (
              <p className="text-xs text-muted-foreground mt-1">
                {stats.state.progress.current_item}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="diagnostics" data-testid="tab-diagnostics">Diagnostics</TabsTrigger>
          <TabsTrigger value="obs-dates" data-testid="tab-obs-dates">Obs Dates</TabsTrigger>
          <TabsTrigger value="buckets" data-testid="tab-buckets">Probability Buckets</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Logs</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Run History</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">Configuration</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Funds Analyzed</p>
                    <p className="text-2xl font-bold">{stats?.funds_count || 0}</p>
                  </div>
                  <Brain className="w-8 h-8 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Sectors</p>
                    <p className="text-2xl font-bold">{stats?.sectors_count || 0}</p>
                  </div>
                  <Target className="w-8 h-8 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">12M Skill Rows</p>
                    <p className="text-2xl font-bold">{stats?.collections?.skill_12m_rows?.toLocaleString() || 0}</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Timing Curves</p>
                    <p className="text-2xl font-bold">{stats?.collections?.curve_rows?.toLocaleString() || 0}</p>
                  </div>
                  <LineChart className="w-8 h-8 text-primary/20" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Date Range & Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Data Coverage</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Oldest Observation</span>
                  <span className="font-mono">{formatDate(stats?.date_range?.oldest)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Latest Observation</span>
                  <span className="font-mono">{formatDate(stats?.date_range?.newest)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">6M Skill Rows</span>
                  <span className="font-mono">{stats?.collections?.skill_6m_rows?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Validated Fund-Quarters</span>
                  <span className="font-mono">{stats?.collections?.validated_fund_quarters?.toLocaleString() || 0}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Pipeline Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant={stats?.state?.status === "running" ? "default" : "secondary"}>
                    {stats?.state?.status || "idle"}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Run</span>
                  <span className="font-mono">{formatDate(stats?.state?.last_run_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Status</span>
                  <Badge variant={stats?.state?.last_run_status === "completed" ? "default" : "destructive"}>
                    {stats?.state?.last_run_status || "N/A"}
                  </Badge>
                </div>
                {stats?.state?.status !== "running" && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-2"
                    onClick={handleForceReset}
                  >
                    Force Reset
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Test Card */}
          <Card className="border-emerald-500/20 bg-emerald-500/5">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Rocket className="w-5 h-5 text-emerald-500" />
                    Quick Test Mode
                  </CardTitle>
                  <CardDescription>
                    Rapidly verify pipeline functionality with reduced data (1-3 minutes)
                  </CardDescription>
                </div>
                <Button
                  onClick={handleQuickTest}
                  disabled={running || quickTestLoading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="quick-test-btn"
                >
                  {quickTestLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Rocket className="w-4 h-4 mr-2" />
                  )}
                  Run Quick Test
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Observation Dates */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Observation Dates</Label>
                    <span className="text-sm font-mono text-emerald-500">{quickTestObsDates}</span>
                  </div>
                  <Slider
                    value={[quickTestObsDates]}
                    onValueChange={(val) => setQuickTestObsDates(val[0])}
                    min={1}
                    max={5}
                    step={1}
                    className="w-full"
                    disabled={running}
                  />
                  <p className="text-xs text-muted-foreground">
                    How many observation dates to process (1 = fastest)
                  </p>
                </div>

                {/* Max Funds */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Max Funds</Label>
                    <span className="text-sm font-mono text-emerald-500">{quickTestMaxFunds}</span>
                  </div>
                  <Slider
                    value={[quickTestMaxFunds]}
                    onValueChange={(val) => setQuickTestMaxFunds(val[0])}
                    min={5}
                    max={100}
                    step={5}
                    className="w-full"
                    disabled={running}
                  />
                  <p className="text-xs text-muted-foreground">
                    Limit funds for faster processing
                  </p>
                </div>

                {/* ADVI Steps */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">ADVI Steps</Label>
                    <span className="text-sm font-mono text-emerald-500">{quickTestAdviSteps.toLocaleString()}</span>
                  </div>
                  <Slider
                    value={[quickTestAdviSteps]}
                    onValueChange={(val) => setQuickTestAdviSteps(val[0])}
                    min={1000}
                    max={25000}
                    step={1000}
                    className="w-full"
                    disabled={running}
                  />
                  <p className="text-xs text-muted-foreground">
                    PyMC optimization iterations (5000 = fast, 25000 = accurate)
                  </p>
                </div>
              </div>

              <div className="mt-4 p-3 rounded-lg bg-muted/50 text-sm">
                <span className="font-medium">Estimated time: </span>
                <span className="text-emerald-500">
                  {quickTestObsDates === 1 && quickTestMaxFunds <= 20 && quickTestAdviSteps <= 5000 
                    ? "~1-2 minutes" 
                    : quickTestObsDates <= 2 && quickTestMaxFunds <= 50 && quickTestAdviSteps <= 10000
                    ? "~3-5 minutes"
                    : "~5-15 minutes"}
                </span>
                <span className="text-muted-foreground ml-2">
                  (depends on data volume and server load)
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Modal Cloud Card */}
          <Card className="border-blue-500/20 bg-blue-500/5">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Cloud className="w-5 h-5 text-blue-500" />
                    Modal Cloud Compute
                  </CardTitle>
                  <CardDescription>
                    Offload heavy PyMC Bayesian computation to Modal's cloud infrastructure
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${useModal ? 'text-blue-500' : 'text-muted-foreground'}`}>
                    {useModal ? 'Enabled' : 'Disabled'}
                  </span>
                  <Switch
                    checked={useModal}
                    onCheckedChange={async (checked) => {
                      setUseModal(checked);
                      // Persist to backend
                      try {
                        await fetch(`${API}/fund-intelligence/config`, {
                          method: 'POST',
                          headers: { 
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}` 
                          },
                          body: JSON.stringify({ use_modal: checked })
                        });
                      } catch (e) {
                        console.error('Failed to save Modal setting:', e);
                      }
                    }}
                    disabled={running}
                    data-testid="use-modal-toggle"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {useModal && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Memory Slider */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Memory (RAM)</Label>
                      <span className="text-sm font-mono text-blue-500">{modalMemoryGb} GB</span>
                    </div>
                    <Slider
                      value={[modalMemoryGb]}
                      onValueChange={(val) => setModalMemoryGb(val[0])}
                      onValueCommit={async (val) => {
                        try {
                          const res = await fetch(`${API}/fund-intelligence/config`, {
                            method: 'POST',
                            headers: { 
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}` 
                            },
                            body: JSON.stringify({ modal_memory_gb: val[0] })
                          });
                          if (res.ok) {
                            toast.success(`Memory set to ${val[0]}GB`);
                          }
                        } catch (e) {
                          toast.error('Failed to save memory setting');
                        }
                      }}
                      min={8}
                      max={128}
                      step={8}
                      className="w-full"
                      disabled={running}
                    />
                    <p className="text-xs text-muted-foreground">
                      8GB (small) → 128GB (large models with 1000+ funds)
                    </p>
                  </div>

                  {/* CPU Slider */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">CPUs</Label>
                      <span className="text-sm font-mono text-blue-500">{modalCpu} cores</span>
                    </div>
                    <Slider
                      value={[modalCpu]}
                      onValueChange={(val) => setModalCpu(val[0])}
                      onValueCommit={async (val) => {
                        try {
                          const res = await fetch(`${API}/fund-intelligence/config`, {
                            method: 'POST',
                            headers: { 
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}` 
                            },
                            body: JSON.stringify({ modal_cpu: val[0] })
                          });
                          if (res.ok) {
                            toast.success(`CPUs set to ${val[0]} cores`);
                          }
                        } catch (e) {
                          toast.error('Failed to save CPU setting');
                        }
                      }}
                      min={2}
                      max={16}
                      step={2}
                      className="w-full"
                      disabled={running}
                    />
                    <p className="text-xs text-muted-foreground">
                      2 (economy) → 16 (parallel ADVI optimization)
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-500 shrink-0">✓</span>
                  <div>
                    <span className="font-medium">{modalMemoryGb}GB RAM + {modalCpu} CPUs</span>
                    <p className="text-muted-foreground">Configurable resources for your workload</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-500 shrink-0">✓</span>
                  <div>
                    <span className="font-medium">Auto-scaling</span>
                    <p className="text-muted-foreground">Process multiple observation dates in parallel</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-500 shrink-0">✓</span>
                  <div>
                    <span className="font-medium">Pay-per-second</span>
                    <p className="text-muted-foreground">Only pay for actual compute time used</p>
                  </div>
                </div>
              </div>

              {/* Cost estimate */}
              {useModal && (
                <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm">
                  <span className="text-blue-500 font-medium">Estimated cost:</span>
                  <span className="text-muted-foreground ml-2">
                    ~${((modalMemoryGb * 0.000015 + modalCpu * 0.000125) * 60).toFixed(3)}/min 
                    ({modalMemoryGb}GB @ $0.000015/GB-sec + {modalCpu} CPUs @ $0.000125/CPU-sec)
                  </span>
                </div>
              )}
              
              {/* NEW: Direct DB Load Toggle (Option C) */}
              {useModal && (
                <div className="mt-4 p-4 rounded-lg border border-border bg-card">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-purple-500" />
                      <span className="font-medium text-sm">Direct DB Load Mode</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-500">BETA</span>
                    </div>
                    <Switch
                      checked={modalDirectDbLoad}
                      onCheckedChange={async (checked) => {
                        setModalDirectDbLoad(checked);
                        try {
                          await fetch(`${API}/fund-intelligence/config`, {
                            method: 'POST',
                            headers: { 
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}` 
                            },
                            body: JSON.stringify({ modal_direct_db_load: checked })
                          });
                          toast.success(checked ? 'Direct DB mode enabled' : 'Standard mode enabled');
                        } catch (e) {
                          toast.error('Failed to save setting');
                        }
                      }}
                      disabled={running}
                      data-testid="modal-direct-db-toggle"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    {modalDirectDbLoad ? (
                      <span className="text-purple-500">
                        <strong>ON:</strong> Modal loads data directly from MongoDB (faster for large rebuilds)
                      </span>
                    ) : (
                      <span>
                        <strong>OFF:</strong> Backend loads data, sends to Modal (standard mode)
                      </span>
                    )}
                  </p>
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <span className={modalDirectDbLoad ? 'text-purple-500' : ''}>
                        {modalDirectDbLoad ? '✓' : '○'} No backend→Modal transfer
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={modalDirectDbLoad ? 'text-purple-500' : ''}>
                        {modalDirectDbLoad ? '✓' : '○'} Lower memory pressure
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={modalDirectDbLoad ? 'text-purple-500' : ''}>
                        {modalDirectDbLoad ? '✓' : '○'} Parallel data loading
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {!useModal && (
                <div className="mt-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-sm">
                  <span className="text-yellow-500 font-medium">Local Mode:</span>
                  <span className="text-muted-foreground ml-2">
                    PyMC will run on the server. Large models (900+ funds) may timeout or run slowly.
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Metric Definitions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Key Metrics Explained</CardTitle>
              <CardDescription>
                Hover over metrics in the data table for detailed explanations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(METRIC_DEFINITIONS).map(([key, definition]) => (
                  <div key={key} className="p-3 rounded-lg bg-muted/50">
                    <h4 className="font-medium text-sm mb-1">{key.replace(/_/g, " ").toUpperCase()}</h4>
                    <p className="text-xs text-muted-foreground">{definition}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Diagnostics Tab */}
        <TabsContent value="diagnostics" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Pre-Flight Check
                  </CardTitle>
                  <CardDescription>
                    Validate data compatibility before running the pipeline
                  </CardDescription>
                </div>
                <Button 
                  onClick={runPreflightCheck} 
                  disabled={preflightLoading}
                  data-testid="preflight-check-btn"
                >
                  {preflightLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Shield className="w-4 h-4 mr-2" />
                  )}
                  Run Preflight Check
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {preflightCheck ? (
                <div className="space-y-6">
                  {/* Status Summary */}
                  <div className={`p-4 rounded-lg ${
                    preflightCheck.status === "ready" 
                      ? "bg-green-500/10 border border-green-500/30" 
                      : "bg-yellow-500/10 border border-yellow-500/30"
                  }`}>
                    <div className="flex items-center gap-2">
                      {preflightCheck.status === "ready" ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      )}
                      <span className="font-semibold">
                        {preflightCheck.status === "ready" ? "Ready to Run" : "Issues Found"}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {preflightCheck.summary}
                    </p>
                  </div>

                  {/* Issues */}
                  {preflightCheck.issues?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-red-400 flex items-center gap-2">
                        <XCircle className="w-4 h-4" />
                        Issues ({preflightCheck.issues.length})
                      </h4>
                      <div className="space-y-2">
                        {preflightCheck.issues.map((issue, idx) => (
                          <div key={idx} className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm">
                            {issue}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {preflightCheck.recommendations?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-blue-400 flex items-center gap-2">
                        <HelpCircle className="w-4 h-4" />
                        Recommendations
                      </h4>
                      <div className="space-y-2">
                        {preflightCheck.recommendations.map((rec, idx) => (
                          <div key={idx} className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
                            {rec}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Data Checks */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Buy Events */}
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h5 className="font-semibold mb-2 flex items-center gap-2">
                        <Database className="w-4 h-4" />
                        Buy Events
                      </h5>
                      {preflightCheck.checks?.buy_events && (
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Count:</span>
                            <span>{preflightCheck.checks.buy_events.count?.toLocaleString() || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Date Range:</span>
                            <span className="font-mono text-xs">{preflightCheck.checks.buy_events.date_range || "N/A"}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Symbols:</span>
                            <span>{preflightCheck.checks.buy_events.unique_symbols?.toLocaleString() || 0}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Alpha Data */}
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h5 className="font-semibold mb-2 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Stock Alpha
                      </h5>
                      {preflightCheck.checks?.stock_alpha && (
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Count:</span>
                            <span>{preflightCheck.checks.stock_alpha.count?.toLocaleString() || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Date Range:</span>
                            <span className="font-mono text-xs">{preflightCheck.checks.stock_alpha.date_range || "N/A"}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Symbols:</span>
                            <span>{preflightCheck.checks.stock_alpha.unique_symbols?.toLocaleString() || 0}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Symbol Overlap */}
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h5 className="font-semibold mb-2 flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Symbol Overlap
                      </h5>
                      {preflightCheck.checks?.symbol_overlap && (
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Buy Symbols:</span>
                            <span>{preflightCheck.checks.symbol_overlap.buy_symbols?.toLocaleString() || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Alpha Symbols:</span>
                            <span>{preflightCheck.checks.symbol_overlap.alpha_symbols?.toLocaleString() || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Overlap:</span>
                            <span className={preflightCheck.checks.symbol_overlap.overlap < 50 ? "text-red-400" : "text-green-400"}>
                              {preflightCheck.checks.symbol_overlap.overlap?.toLocaleString() || 0} ({preflightCheck.checks.symbol_overlap.overlap_pct || 0}%)
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Window Analysis */}
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h5 className="font-semibold mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Window Analysis
                      </h5>
                      {preflightCheck.checks?.window_analysis && (
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Lag Days:</span>
                            <span>{preflightCheck.checks.window_analysis.config_lag_days}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Window:</span>
                            <span className="font-mono text-xs">
                              {preflightCheck.checks.window_analysis.window_start} to {preflightCheck.checks.window_analysis.window_end}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Buy Events:</span>
                            <span className="font-mono text-xs">
                              {preflightCheck.checks.window_analysis.buy_events_start} to {preflightCheck.checks.window_analysis.buy_events_end}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Click "Run Preflight Check" to validate your data before running the pipeline.</p>
                  <p className="text-sm mt-2">This will check buy events, alpha data, symbol overlap, and window compatibility.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Observation Dates Management Tab */}
        <TabsContent value="obs-dates" className="space-y-4">
          {/* Run Specific Dates Card */}
          <Card className="border-blue-500/20 bg-blue-500/5">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                Run Specific Observation Dates
              </CardTitle>
              <CardDescription>
                Select one or more dates to process individually
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4">
                {availableDates.slice(0, 20).map((d, idx) => (
                  <Button
                    key={idx}
                    variant={selectedDates.includes(d.date) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleDateSelection(d.date)}
                    disabled={running}
                    className={`font-mono text-xs ${d.processed ? 'border-green-500/50' : ''}`}
                  >
                    {d.date}
                    {d.processed && <CheckCircle className="w-3 h-3 ml-1 text-green-500" />}
                  </Button>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {selectedDates.length > 0 
                    ? `${selectedDates.length} date(s) selected`
                    : "Select dates to process (most recent shown first)"}
                </div>
                <div className="flex gap-2">
                  {selectedDates.length > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedDates([])}
                    >
                      Clear
                    </Button>
                  )}
                  <Button
                    onClick={handleRunSelectedDates}
                    disabled={running || selectedDates.length === 0}
                    size="sm"
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Run Selected ({selectedDates.length})
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Processed Dates Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Processed Observation Dates</CardTitle>
                  <CardDescription>
                    Track, delete, or reprocess specific observation dates
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchObsDateStatus}
                  disabled={obsDateLoading}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${obsDateLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {obsDateStatus.length > 0 ? (
                <div className="space-y-4">
                  <div className="text-sm text-muted-foreground mb-4">
                    {obsDateStatus.length} observation dates tracked. 
                    Skills computed twice per year (May 15 & Nov 14).
                  </div>
                  <ScrollArea className="h-[400px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-card border-b">
                        <tr>
                          <th className="text-left p-2">Observation Date</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-right p-2">12M Skills</th>
                          <th className="text-right p-2">6M Skills</th>
                          <th className="text-right p-2">Curves</th>
                          <th className="text-left p-2">Last Processed</th>
                          <th className="text-left p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {obsDateStatus.map((obs, idx) => (
                          <tr key={idx} className="border-b hover:bg-muted/50">
                            <td className="p-2 font-mono">{obs.observation_date}</td>
                            <td className="p-2">
                              <Badge 
                                variant={obs.status === "completed" ? "default" : 
                                         obs.status === "deleted" ? "destructive" : "secondary"}
                              >
                                {obs.status}
                              </Badge>
                            </td>
                            <td className="p-2 text-right font-mono">{obs.skill_12m_count || 0}</td>
                            <td className="p-2 text-right font-mono">{obs.skill_6m_count || 0}</td>
                            <td className="p-2 text-right font-mono">{obs.curve_count || 0}</td>
                            <td className="p-2 text-xs text-muted-foreground">
                              {obs.last_processed_at ? new Date(obs.last_processed_at).toLocaleString() : '-'}
                            </td>
                            <td className="p-2">
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleReprocessObsDate(obs.observation_date)}
                                  disabled={running}
                                  title="Reprocess this observation date"
                                >
                                  <RefreshCw className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteObsDate(obs.observation_date)}
                                  disabled={running}
                                  title="Delete all data for this date"
                                >
                                  <XCircle className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No observation dates have been processed yet.</p>
                  <p className="text-sm mt-2">Select dates above or run the pipeline to start tracking.</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">How Updates Work</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">1</div>
                <div>
                  <strong>Full Run:</strong> Processes the last {config.max_observation_dates || 10} observation dates (~{(config.max_observation_dates || 10) / 2} years). 
                  Existing data is updated via upsert.
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">2</div>
                <div>
                  <strong>Quick Test:</strong> Processes only 1-5 recent observation dates with reduced funds for rapid validation.
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">3</div>
                <div>
                  <strong>Reprocess:</strong> Re-runs a specific observation date (useful after data fixes or model changes).
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                  <CheckCircle className="w-3 h-3" />
                </div>
                <div>
                  <strong>In 6 months:</strong> New observation dates (May/Nov) will be automatically included in the next Full Run.
                  Previous dates remain unless deleted.
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="buckets" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Outcome Probability Buckets</CardTitle>
                  <CardDescription>
                    Posterior probability distribution across 8 outcome buckets (based on 1 + alpha)
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchSkillData}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Bucket Legend */}
              <div className="flex flex-wrap gap-2 mb-4 p-3 bg-muted/50 rounded-lg">
                {BUCKET_LABELS.map((label, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    b{idx}: {label}
                  </Badge>
                ))}
              </div>

              {/* 12-Month Skills Section */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  12-Month Forward Alpha Skills
                  <Badge variant="secondary">{skillData.length} rows</Badge>
                </h3>
                <ScrollArea className="h-[300px] border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-card border-b">
                      <tr>
                        <th className="text-left p-2">Obs Date</th>
                        <th className="text-left p-2">Fund</th>
                        <th className="text-left p-2">Sector</th>
                        {BUCKET_LABELS.map((_, idx) => (
                          <th key={idx} className="text-right p-2 text-xs">b{idx}</th>
                        ))}
                        <th className="text-right p-2">E[α]</th>
                        <th className="text-right p-2">ESS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skillData.map((row, idx) => (
                        <tr key={idx} className="border-b hover:bg-muted/50">
                          <td className="p-2 font-mono text-xs">{row.observation_date}</td>
                          <td className="p-2 font-mono text-xs">{row.fund}</td>
                          <td className="p-2">{row.sector}</td>
                          {[0,1,2,3,4,5,6,7].map(b => {
                            const val = row[`b${b}`];
                            const pct = val ? (val * 100).toFixed(1) : "0.0";
                            const intensity = val ? Math.min(val * 2, 1) : 0;
                            const bgColor = b < 4 
                              ? `rgba(239, 68, 68, ${intensity * 0.3})` 
                              : `rgba(34, 197, 94, ${intensity * 0.3})`;
                            return (
                              <td 
                                key={b} 
                                className="p-2 text-right font-mono text-xs"
                                style={{ backgroundColor: bgColor }}
                              >
                                {pct}%
                              </td>
                            );
                          })}
                          <td className="p-2 text-right font-mono text-xs">
                            {formatNumber(row.expected_alpha_12m * 100, 1)}%
                          </td>
                          <td className="p-2 text-right font-mono text-xs">
                            {formatNumber(row.ess_proxy, 1)}
                          </td>
                        </tr>
                      ))}
                      {skillData.length === 0 && (
                        <tr>
                          <td colSpan={12} className="p-4 text-center text-muted-foreground">
                            No 12m skill data available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>

              {/* 6-Month Skills Section */}
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  6-Month Forward Alpha Skills
                  <Badge variant="secondary">{skill6mData.length} rows</Badge>
                </h3>
                <ScrollArea className="h-[300px] border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-card border-b">
                      <tr>
                        <th className="text-left p-2">Obs Date</th>
                        <th className="text-left p-2">Fund</th>
                        <th className="text-left p-2">Sector</th>
                        {BUCKET_LABELS.map((_, idx) => (
                          <th key={idx} className="text-right p-2 text-xs">b{idx}</th>
                        ))}
                        <th className="text-right p-2">E[α]</th>
                        <th className="text-right p-2">ESS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skill6mData.map((row, idx) => (
                        <tr key={idx} className="border-b hover:bg-muted/50">
                          <td className="p-2 font-mono text-xs">{row.observation_date}</td>
                          <td className="p-2 font-mono text-xs">{row.fund}</td>
                          <td className="p-2">{row.sector}</td>
                          {[0,1,2,3,4,5,6,7].map(b => {
                            const val = row[`b${b}`];
                            const pct = val ? (val * 100).toFixed(1) : "0.0";
                            const intensity = val ? Math.min(val * 2, 1) : 0;
                            const bgColor = b < 4 
                              ? `rgba(239, 68, 68, ${intensity * 0.3})` 
                              : `rgba(34, 197, 94, ${intensity * 0.3})`;
                            return (
                              <td 
                                key={b} 
                                className="p-2 text-right font-mono text-xs"
                                style={{ backgroundColor: bgColor }}
                              >
                                {pct}%
                              </td>
                            );
                          })}
                          <td className="p-2 text-right font-mono text-xs">
                            {formatNumber(row.expected_alpha_6m * 100, 1)}%
                          </td>
                          <td className="p-2 text-right font-mono text-xs">
                            {formatNumber(row.ess_proxy, 1)}
                          </td>
                        </tr>
                      ))}
                      {skill6mData.length === 0 && (
                        <tr>
                          <td colSpan={12} className="p-4 text-center text-muted-foreground">
                            No 6m skill data available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Pipeline Logs</CardTitle>
                  <CardDescription>
                    Real-time logs for debugging pipeline runs
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    className="p-2 rounded-md bg-background border text-sm"
                    value={selectedRunId}
                    onChange={(e) => setSelectedRunId(e.target.value)}
                  >
                    <option value="">Select a run...</option>
                    {runHistory.map(run => (
                      <option key={run.run_id} value={run.run_id}>
                        {run.run_id} - {run.run_type} ({run.status})
                      </option>
                    ))}
                  </select>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => fetchRunLogs(selectedRunId)}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px]">
                <div className="space-y-1 font-mono text-xs">
                  {runLogs.map((log, idx) => {
                    const levelColor = 
                      log.level === "error" ? "text-red-500 bg-red-500/10" :
                      log.level === "warning" ? "text-yellow-500 bg-yellow-500/10" :
                      "text-muted-foreground";
                    const timestamp = log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : "";
                    return (
                      <div 
                        key={idx} 
                        className={`p-2 rounded ${levelColor} ${log.level === "error" ? "border border-red-500/20" : ""}`}
                      >
                        <span className="text-muted-foreground">[{timestamp}]</span>
                        <span className={`ml-2 uppercase font-semibold ${
                          log.level === "error" ? "text-red-500" :
                          log.level === "warning" ? "text-yellow-500" : "text-blue-500"
                        }`}>
                          [{log.level}]
                        </span>
                        <span className="ml-2">{log.message}</span>
                        {log.data && Object.keys(log.data).length > 0 && (
                          <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-x-auto">
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        )}
                      </div>
                    );
                  })}
                  {runLogs.length === 0 && (
                    <div className="p-8 text-center text-muted-foreground">
                      {selectedRunId ? "No logs for this run." : "Select a run to view logs."}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Run History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Pipeline Run History</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card border-b">
                    <tr>
                      <th className="text-left p-2">Run ID</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Started</th>
                      <th className="text-right p-2">Duration</th>
                      <th className="text-right p-2">Obs Dates</th>
                      <th className="text-right p-2">Funds</th>
                      <th className="text-right p-2">12M Rows</th>
                      <th className="text-right p-2">Curves</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runHistory.map((run, idx) => (
                      <tr key={idx} className="border-b hover:bg-muted/50">
                        <td className="p-2 font-mono text-xs">{run.run_id}</td>
                        <td className="p-2">
                          <Badge variant="outline">{run.run_type}</Badge>
                        </td>
                        <td className="p-2">
                          <Badge variant={
                            run.status === "completed" ? "default" :
                            run.status === "running" ? "secondary" :
                            run.status === "failed" ? "destructive" : "outline"
                          }>
                            {run.status}
                          </Badge>
                        </td>
                        <td className="p-2 text-xs">{formatDate(run.started_at)}</td>
                        <td className="p-2 text-right font-mono text-xs">
                          {run.duration_seconds ? `${run.duration_seconds}s` : "-"}
                        </td>
                        <td className="p-2 text-right">{run.observation_dates_processed || 0}</td>
                        <td className="p-2 text-right">{run.funds_processed || 0}</td>
                        <td className="p-2 text-right">{run.skill_12m_rows || 0}</td>
                        <td className="p-2 text-right">{run.curve_rows || 0}</td>
                      </tr>
                    ))}
                    {runHistory.length === 0 && (
                      <tr>
                        <td colSpan={9} className="p-8 text-center text-muted-foreground">
                          No runs yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Validated Fund Universe */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Validated Fund Universe</CardTitle>
                <CardDescription>
                  Rules for determining which funds are eligible for skill estimation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Min Holdings per Quarter</Label>
                    <Input
                      type="number"
                      value={config.min_holdings || 5}
                      onChange={(e) => handleConfigChange("min_holdings", parseInt(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Max Holdings per Quarter</Label>
                    <Input
                      type="number"
                      value={config.max_holdings || 125}
                      onChange={(e) => handleConfigChange("max_holdings", parseInt(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Rolling Window (Quarters)</Label>
                  <Input
                    type="number"
                    value={config.window_quarters || 12}
                    onChange={(e) => handleConfigChange("window_quarters", parseInt(e.target.value))}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Fund must have N consecutive quarters with valid holdings count
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Model Parameters */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Bayesian Model Parameters</CardTitle>
                <CardDescription>
                  Hierarchical model configuration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Lambda Mix (Fund vs Sector)</Label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={config.lambda_mix || 0.65}
                      onChange={(e) => handleConfigChange("lambda_mix", parseFloat(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Kappa (Concentration)</Label>
                    <Input
                      type="number"
                      step="1"
                      value={config.kappa || 10}
                      onChange={(e) => handleConfigChange("kappa", parseFloat(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">ADVI Steps</Label>
                    <Input
                      type="number"
                      value={config.advi_steps || 25000}
                      onChange={(e) => handleConfigChange("advi_steps", parseInt(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Min Buys per Fund</Label>
                    <Input
                      type="number"
                      value={config.min_buys_per_fund_global || 10}
                      onChange={(e) => handleConfigChange("min_buys_per_fund_global", parseInt(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Processing Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Processing Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Lookback Months</Label>
                    <Input
                      type="number"
                      value={config.lookback_months || 36}
                      onChange={(e) => handleConfigChange("lookback_months", parseInt(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Filing Lag Days</Label>
                    <Input
                      type="number"
                      value={config.filing_lag_days || 5}
                      onChange={(e) => handleConfigChange("filing_lag_days", parseInt(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Max Observation Dates per Run</Label>
                  <Input
                    type="number"
                    value={config.max_observation_dates || 20}
                    onChange={(e) => handleConfigChange("max_observation_dates", parseInt(e.target.value))}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {configDirty && (
            <div className="flex justify-end">
              <Button onClick={saveConfig} data-testid="save-config-btn">
                <CheckCircle className="w-4 h-4 mr-2" />
                Save Configuration
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SkillsPage;
